import React, { useState, useRef } from "react";
import { trackMerchantApplicationStarted, trackMerchantApplicationSubmitted } from "@/lib/analytics";
import { Link } from "wouter";
import { useSubmitApplication } from "@workspace/api-client-react";
import type { ApplicationInputServiceMethodsItem } from "@workspace/api-client-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Building, ShieldAlert } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const applicationSchema = z.object({
  contactPerson: z.string().min(2, "Required"),
  businessName: z.string().min(2, "Required"),
  email: z.string().email("Invalid email"),
  phone: z.string().min(5, "Required"),
  whatsapp: z.string().optional(),
  category: z.string().min(1, "Required"),
  area: z.string().min(1, "Required"),
  websiteOrInstagram: z.string().optional(),
  description: z.string().min(20, "Please provide a brief description (min 20 chars)"),
  acceptsUsdt: z.boolean(),
  supportedNetwork: z.string().optional(),
  serviceMethods: z.array(z.enum(["merchant_location", "customer_location", "physical_goods", "delivery", "pickup", "online_remote"])).min(1, "Select at least one service method"),
  planType: z.enum(["basic", "featured"]),
  tradeLicence: z.string().optional(),
  termsAccepted: z.boolean().refine((value) => value, "You must accept the terms"),
});

const SERVICE_METHODS = [
  ["merchant_location", "Service at Merchant Location"],
  ["customer_location", "Service at Customer Location / Home Service"],
  ["physical_goods", "Physical Goods"],
  ["delivery", "Delivery Available"],
  ["pickup", "Pickup Available"],
  ["online_remote", "Online / Remote Service"],
] as const;

type ApplicationFormValues = z.infer<typeof applicationSchema>;

function normalizeOptional(value: string | undefined) {
  const normalized = value?.trim();
  return normalized || undefined;
}

export default function ListYourBusinessPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const initialPlan = (searchParams.get("plan") as "basic"|"featured") || "basic";
  const { t } = useI18n();

  const { toast } = useToast();
  const [reference, setReference] = useState<string | null>(null);
  const submitApp = useSubmitApplication();
  const startedTracked = useRef(false);

  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      contactPerson: "",
      businessName: "",
      email: "",
      phone: "",
      whatsapp: "",
      category: "",
      area: "",
      websiteOrInstagram: "",
      description: "",
      acceptsUsdt: true,
      supportedNetwork: "TRC20, ERC20",
      serviceMethods: [],
      planType: initialPlan,
      tradeLicence: "",
    },
  });

  const onSubmit = (values: ApplicationFormValues) => {
    submitApp.mutate({ data: {
      ...values,
      whatsapp: normalizeOptional(values.whatsapp),
      websiteOrInstagram: normalizeOptional(values.websiteOrInstagram),
      tradeLicence: normalizeOptional(values.tradeLicence),
      supportedNetwork: values.acceptsUsdt ? normalizeOptional(values.supportedNetwork) : undefined,
      serviceMethods: values.serviceMethods as ApplicationInputServiceMethodsItem[],
    } }, {
      onSuccess: (result) => {
        trackMerchantApplicationSubmitted({ listing_plan: values.planType });
        setReference(result.reference);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      onError: (error: unknown) => {
        const apiError = error as { data?: { fieldErrors?: Record<string, string> } };
        const errors = apiError.data?.fieldErrors;
        if (errors) {
          Object.entries(errors).forEach(([field, message]) => {
            form.setError(field as keyof ApplicationFormValues, { message });
          });
          return;
        }
        toast({
          title: t("Submission failed"),
          description: t("There was an error submitting your application. Please try again."),
          variant: "destructive"
        });
      }
    });
  };

  if (reference) {
    return (
      <div className="flex-1 container mx-auto px-4 py-24 max-w-3xl text-center">
        <div className="w-24 h-24 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-8">
          <CheckCircle2 className="h-12 w-12" />
        </div>
        <h1 className="text-4xl font-extrabold mb-4" data-testid="text-application-submitted">{t("Application Submitted!")}</h1>
        <p className="text-xl text-muted-foreground mb-8">
          {t("Thank you for applying to join Souq Crypto. Our team will review your application and contact you within 24–48 hours.")}
        </p>
        <div className="rounded-xl bg-muted border border-border px-5 py-4 mb-8 inline-block">
          <p className="text-sm text-muted-foreground">{t("Application reference")}</p>
          <p className="font-mono font-bold text-xl" data-testid="text-application-reference">{reference}</p>
        </div>
        <Button size="lg" asChild data-testid="btn-return-homepage">
          <Link href="/">{t("Return to Homepage")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-muted/20 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-4 bg-primary/10 text-primary border-primary/20 px-4 py-1 text-sm">
            <Building className="mr-2 h-4 w-4 inline"/> {t("Merchant Application")}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">{t("List Your Business")}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("Connect with customers searching for USDT-friendly businesses in Dubai.")}
          </p>
        </div>

        <Card className="border-border shadow-xl shadow-black/5">
          <CardContent className="p-6 md:p-10">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
                onFocus={() => {
                  if (startedTracked.current) return;
                  startedTracked.current = true;
                  trackMerchantApplicationStarted({ listing_plan: form.getValues("planType") });
                }}
              >

                {/* Section 1: Business Details */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold border-b border-border pb-2">{t("Business Details")}</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="businessName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Business Name")} *</FormLabel>
                          <FormControl><Input placeholder="E.g. Luxury Motors Dubai" {...field} data-testid="input-business-name" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="contactPerson"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Contact Person")} *</FormLabel>
                          <FormControl><Input placeholder="Full Name" {...field} data-testid="input-contact-person" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Category")} *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger data-testid="select-category"><SelectValue placeholder={t("Select category")} /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="Electronics">{t("Electronics")}</SelectItem>
                              <SelectItem value="Cars & Rentals">{t("Cars & Rentals")}</SelectItem>
                              <SelectItem value="Beauty & Barber">{t("Beauty & Barber")}</SelectItem>
                              <SelectItem value="Medical & Wellness">{t("Medical & Wellness")}</SelectItem>
                              <SelectItem value="Restaurants & Cafés">{t("Restaurants & Cafés")}</SelectItem>
                              <SelectItem value="Hotels & Travel">{t("Hotels & Travel")}</SelectItem>
                              <SelectItem value="Professional Services">{t("Professional Services")}</SelectItem>
                              <SelectItem value="Shopping & Lifestyle">{t("Shopping & Lifestyle")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="area"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Dubai Area")} *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger data-testid="select-area"><SelectValue placeholder={t("Select area")} /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="Downtown Dubai">{t("Downtown Dubai")}</SelectItem>
                              <SelectItem value="Business Bay">{t("Business Bay")}</SelectItem>
                              <SelectItem value="Dubai Marina">{t("Dubai Marina")}</SelectItem>
                              <SelectItem value="JLT">JLT</SelectItem>
                              <SelectItem value="Meydan">{t("Meydan")}</SelectItem>
                              <SelectItem value="Jumeirah">{t("Jumeirah")}</SelectItem>
                              <SelectItem value="Al Barsha">{t("Al Barsha")}</SelectItem>
                              <SelectItem value="DIFC">DIFC</SelectItem>
                              <SelectItem value="Palm Jumeirah">{t("Palm Jumeirah")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("Business Description")} *</FormLabel>
                        <FormControl>
                          <Textarea placeholder={t("Describe what you offer...")} className="h-24" {...field} data-testid="textarea-description" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Section 2: Contact Information */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold border-b border-border pb-2 mt-8">{t("Contact Information")}</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Email Address")} *</FormLabel>
                          <FormControl><Input type="email" placeholder="contact@business.com" {...field} data-testid="input-email" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Phone Number")} *</FormLabel>
                          <FormControl><Input placeholder="+971 50 000 0000" {...field} data-testid="input-phone" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="whatsapp"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("WhatsApp Number")}</FormLabel>
                          <FormControl><Input placeholder="+971 50 000 0000" {...field} data-testid="input-whatsapp" /></FormControl>
                          <FormDescription>{t("Highly recommended for fast conversions.")}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="websiteOrInstagram"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Website or Instagram Link")}</FormLabel>
                          <FormControl><Input placeholder="https://..." {...field} data-testid="input-website" /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Section 3: Crypto Details */}
                <div className="space-y-6 bg-primary/5 p-6 rounded-xl border border-primary/20 mt-8">
                  <h2 className="text-xl font-bold flex items-center gap-2 text-primary-foreground dark:text-primary">{t("Payment Details")}</h2>

                  <FormField
                    control={form.control}
                    name="acceptsUsdt"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>{t("Do you accept USDT directly?")} *</FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={(val) => field.onChange(val === 'true')} defaultValue={field.value ? 'true' : 'false'} className="flex flex-col space-y-1">
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl><RadioGroupItem value="true" /></FormControl>
                              <FormLabel className="font-normal">{t("Yes, we accept USDT to our own wallet")}</FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl><RadioGroupItem value="false" /></FormControl>
                              <FormLabel className="font-normal">{t("No, not currently.")}</FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch("acceptsUsdt") && (
                    <FormField
                      control={form.control}
                      name="supportedNetwork"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Supported Networks")}</FormLabel>
                          <FormControl><Input placeholder="E.g. TRC20, ERC20" {...field} data-testid="input-supported-networks" /></FormControl>
                          <FormDescription>{t("Which networks do you accept USDT on?")}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                {/* Section 4: Service methods */}
                <FormField
                  control={form.control}
                  name="serviceMethods"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-xl font-bold">{t("How do you serve customers?")} *</FormLabel>
                      <FormDescription>{t("Select at least one service method")}</FormDescription>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" dir="auto">
                        {SERVICE_METHODS.map(([value, label]) => (
                          <label key={value} className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50">
                            <Checkbox
                              checked={field.value.includes(value)}
                              onCheckedChange={(checked) => field.onChange(
                                checked ? [...field.value, value] : field.value.filter((method) => method !== value),
                              )}
                              data-testid={`checkbox-service-${value}`}
                            />
                            <span className="text-sm font-medium">{t(label)}</span>
                          </label>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Section 5: Plan & Verification */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold border-b border-border pb-2 mt-8">{t("Plan & Verification")}</h2>

                  <FormField
                    control={form.control}
                    name="planType"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>{t("Select Plan")}</FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormItem className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${field.value === 'basic' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                              <FormControl><RadioGroupItem value="basic" className="sr-only" /></FormControl>
                              <div className="flex justify-between items-start mb-2">
                                <FormLabel className="font-bold text-lg cursor-pointer">{t("Basic Listing")}</FormLabel>
                                <div className="text-sm font-bold bg-muted px-2 py-1 rounded">{t("Free")}</div>
                              </div>
                              <FormDescription>{t("Standard visibility, up to 5 listings.")}</FormDescription>
                            </FormItem>

                            <FormItem className={`border-2 rounded-xl p-4 cursor-pointer transition-all relative overflow-hidden ${field.value === 'featured' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'}`}>
                              <FormControl><RadioGroupItem value="featured" className="sr-only" data-testid="radio-plan-featured" /></FormControl>
                              <div className="absolute top-0 right-0 bg-accent text-accent-foreground text-[10px] font-bold px-2 py-0.5 rounded-bl">{t("Recommended")}</div>
                              <div className="flex justify-between items-start mb-2">
                                <FormLabel className="font-bold text-lg cursor-pointer">{t("Featured Partner")}</FormLabel>
                                <div className="text-sm font-bold bg-accent/20 text-accent-foreground px-2 py-1 rounded">{t("AED 499/mo")}</div>
                              </div>
                              <FormDescription>{t("Maximum visibility, featured badges, unlimited listings.")}</FormDescription>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tradeLicence"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("Trade Licence Number (Optional)")}</FormLabel>
                        <FormControl><Input placeholder={t("For internal verification only")} {...field} data-testid="input-trade-licence" /></FormControl>
                        <FormDescription className="flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5"/> {t("Private — not displayed publicly")}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="termsAccepted"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4 bg-muted/50 mt-8">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-terms" />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>{t("I accept the terms and conditions")}</FormLabel>
                          <FormDescription>
                            {t("By submitting this application, you agree to our merchant terms. Souq Crypto is a discovery platform and does not handle any funds.")}
                          </FormDescription>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Disclaimer */}
                <div className="flex items-start gap-3 text-xs text-muted-foreground bg-muted p-4 rounded-xl">
                  <ShieldAlert className="h-4 w-4 shrink-0 text-accent mt-0.5" />
                  <p>{t("Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.")}</p>
                </div>

                <Button type="submit" size="lg" className="w-full h-14 text-lg font-bold shadow-lg" disabled={submitApp.isPending} data-testid="btn-submit-application">
                  {submitApp.isPending ? t("Submitting Application...") : t("Submit Application")}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
